import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { BrandLogo } from "@/components/BrandLogo";
import { NotificationBell } from "@/components/NotificationBell";
import { GlobalSearch } from "@/components/GlobalSearch";
import { InteractionBar } from "@/components/InteractionBar";
import { InstallPrompt } from "@/components/InstallPrompt";
import { useActiveInteraction } from "@/lib/hooks";
import { hasAnyRole, roleLabel } from "@/lib/constants";
import { useTheme } from "next-themes";
import {
  LayoutDashboard,
  PanelsTopLeft,
  CalendarDays,
  Users,
  BarChart3,
  UserCog,
  BookOpen,
  Building2,
  FolderKanban,
  Target,
  Inbox,
  FolderOpen,
  Workflow,
  Receipt,
  LogOut,
  Moon,
  Sun,
  Menu,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/inbox", label: "Caixa de Entrada", icon: Inbox },
  { to: "/workspace", label: "As Minhas Tarefas", icon: PanelsTopLeft },
  { to: "/agenda", label: "Agenda da Equipa", icon: CalendarDays },
  { to: "/clientes", label: "Base de Clientes", icon: Users },
  { to: "/projetos", label: "Projetos e Campanhas", icon: FolderKanban },
  { to: "/comercial", label: "Pipeline Comercial", icon: Target },
  { to: "/hub", label: "Hub Executivo", icon: BarChart3, roles: ["ADMIN"] },
  { to: "/faturacao", label: "Faturação", icon: Receipt, roles: ["ADMIN", "FINANCE"] },
  { to: "/utilizadores", label: "Gestão de Utilizadores", icon: UserCog },
  { to: "/automacoes", label: "Automações", icon: Workflow, roles: ["ADMIN", "SUB_ADMIN"] },
  { to: "/sops", label: "SOPs", icon: BookOpen },
  { to: "/biblioteca", label: "Biblioteca", icon: FolderOpen },
  { to: "/iam-social", label: "I AM Social", icon: Building2 },
];

function BrandMark() {
  return (
    <BrandLogo className="w-full max-w-[190px]" data-testid="brand-mark" />
  );
}

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data: interactionData } = useActiveInteraction();
  // A barra da interação flutua por cima do conteúdo: dá espaço em baixo para
  // não tapar o fim da página.
  const barPadding = interactionData?.interaction ? "pb-28" : "";

  const items = NAV.filter((n) => !n.roles || hasAnyRole(user, n.roles));

  const SidebarContent = () => (
    <>
      <div className="px-5 py-4 flex justify-center">
        <BrandMark />
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.to === "/" ? "workspace" : item.to.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-poppins font-medium transition-colors ${
                isActive
                  ? "bg-primary text-black"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`
            }
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-2">
          <UserAvatar user={user} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-poppins font-semibold text-white truncate">{user.name}</div>
            <div className="text-[11px] text-white/50 truncate">{roleLabel(user)}</div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen pt-6 flex flex-col bg-background">
      {/* Faixa Beta — fixa no topo. O `pt-6` acima reserva-lhe a altura; com
          box-sizing border-box o total continua a ser 100vh. */}
      {/* A faixa é opaca (`bg-background` + tinta por cima): translúcida, via-se
          o conteúdo a passar por baixo ao fazer scroll. */}
      <div className="fixed inset-x-0 top-0 h-6 z-50 flex items-center justify-center border-b border-primary/25 bg-background text-primary text-[10px] font-poppins font-semibold uppercase tracking-[0.15em]">
        <span className="absolute inset-0 bg-primary/15 pointer-events-none" />
        <span className="relative">Beta v1</span>
      </div>

      <div className="flex flex-1 min-h-0">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex w-64 flex-col fixed top-6 bottom-0 left-0 z-30"
        style={{ backgroundColor: "hsl(var(--sidebar))" }}
        data-testid="sidebar"
      >
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside
            className="relative w-64 pt-6 flex flex-col"
            style={{ backgroundColor: "hsl(var(--sidebar))" }}
          >
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 shrink-0 border-b border-border bg-card flex items-center justify-between px-4 lg:px-8">
          <button
            className="lg:hidden p-2 -ml-2 text-foreground"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <GlobalSearch />
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              data-testid="theme-toggle"
              aria-label="Alternar tema"
            >
              {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-muted transition-colors" data-testid="header-user-menu">
                  <UserAvatar user={user} size="h-8 w-8" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <div className="text-sm font-semibold font-poppins">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/perfil")} data-testid="menu-profile">
                  <UserCog className="h-4 w-4 mr-2" /> O Meu Perfil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Terminar Sessão
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className={`flex-1 p-4 lg:p-8 max-w-[1500px] w-full mx-auto animate-fade-in-up ${barPadding}`}>
          <InstallPrompt />
          {children}
        </main>
      </div>
      </div>
      <InteractionBar />
    </div>
  );
}
